'use client';
import { ArrowBack } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { createScreen, uploadImage } from '../data/actions/screens';
import Editor, { Value } from '../packages/editor';
import { flushPendingCellData } from '../packages/editor/core/components/hooks/node';
import { imagePlugin } from '../packages/plugins/content/image';

import { publicFetch } from '../data/services/clientLoaders';
import background from '../packages/plugins/layout/background';
import { customOrder } from '../plugins/cellPlugins';
import draggableContainerPlugin from '../plugins/DraggableLinesPlugins/DraggablesContainer';
import rankPortalTextEditorPlugin from '../plugins/RankPortal/rankPortalTextEditorPlugin';
export default function Home() {
  const [value, setValue] = useState<Value | null>(null);
  // Latest editor value in a ref, so save can read it synchronously right after
  // flushing pending control edits (see handleClick).
  const valueRef = useRef<Value | null>(null);
  const [title, setTitle] = useState('');
  const [uploadImagesId, setUploadImagesId] = useState<number[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<number>(-1);
  const [folders, setFolders] = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);
  // Sync the ref on every editor change synchronously, so a Save that flushes
  // pending edits reads the just-committed value without a re-render.
  const handleEditorChange = useCallback((next: Value | null) => {
    valueRef.current = next;
    setValue(next);
  }, []);

  const path = usePathname();
  const router = useRouter();
  const results: { tenantId: string; companyId: string } = parseUrl(path!)!;

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      // Navigate back if there's a history
      router.back();
    } else {
      // Fallback to default path if no history
      router.push(
        `/admin/tenants/${results.tenantId}/companies/${results.companyId}/screens`
      );
    }
  }, [router]);

  const handleClick = () => {
    // Force any pending control-edit (debounced 200ms) into the store first, so
    // a quick Save keeps the latest edits (image width, link href, …).
    // Deterministic — no timing race like the old setTimeout(300).
    flushPendingCellData();
    const payload = {
      content: JSON.stringify(valueRef.current),
      referenceDocuments: [],
      images: uploadImagesId,
    };
    createScreen(
      title,
      results.tenantId,
      results.companyId,
      payload,
      undefined,
      selectedFolder !== -1 ? selectedFolder?.toString() : ''
    ).then((e) => {
      e?.error
        ? toast.error(e?.error)
        : router.push(path + `/edit/${e.data}/screen`);
    });
  };

  const imageUploadServiceImagePlugin = useCallback(
    () => async (file: any) => {
      const data = await uploadImage(results.tenantId, results.companyId, file);
      setUploadImagesId((prev) => [...prev, data.data.id]);
      return { url: `${process.env.NEXT_PUBLIC_BASE}/${data.data.url}` };
    },
    [results]
  );

  const imageUploadService = useCallback(
    async (file: any) => {
      const data = await uploadImage(results.tenantId, results.companyId, file);
      setUploadImagesId((prev) => [...prev, data.data.id]);
      return { url: `${process.env.NEXT_PUBLIC_BASE}/${data.data.url}` };
    },
    [results]
  );

  const customPlugins = useMemo(
    () =>
      customOrder({
        backgroundPlugin: background,
        imagePlugin: imagePlugin({
          imageUpload: imageUploadServiceImagePlugin(),
        }),
        rankPortalTextEditorPlugin:
          rankPortalTextEditorPlugin(imageUploadService),
        draggablePlugin: draggableContainerPlugin(imageUploadService),
      }),

    []
  );

  const fetchFolders = useCallback(async () => {
    try {
      const response = await publicFetch(
        `${process.env.NEXT_PUBLIC_API_URL}/tenants/${results.tenantId}/companies/${results.companyId}/documents/folders`,
        {}
      );
      setFolders(response);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  }, [results]); // Dependencies ensure it updates when tenant or company changes

  useEffect(() => {
    fetchFolders();
  }, []);

  return (
    <Box pt={6} px={1}>
      <Box width={1}>
        {' '}
        <Button
          onClick={handleBack}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '6px 12px',
            border: '1px solid #007bff',
            borderRadius: '8px',
            color: '#007bff',
            textTransform: 'none',
            backgroundColor: 'white',
            '&:hover': {
              backgroundColor: '#e9f5ff',
              borderColor: '#0056b3',
            },
          }}
        >
          <ArrowBack sx={{ pt: 0 }} />
          Back
        </Button>
      </Box>
      <Typography variant="h2">Create screen</Typography>
      <Box display={'flex'} gap={2}>
        <TextField
          required
          size="small"
          value={title}
          onChange={(e: any) => setTitle(e.target.value)}
          placeholder="Title"
        />
        <Box display={'flex'}>
          <FormControl sx={{ width: '200px', height: 40, display: 'flex' }}>
            <InputLabel id="demo-simple-select-label">Folders</InputLabel>
            <Select
              sx={{ height: 40, width: '100%' }}
              value={selectedFolder} // Ensure empty value is handled
              onChange={({ target }) => setSelectedFolder(+target.value)}
              fullWidth
              label="Folders"
            >
              {/* Default option */}
              <MenuItem value="-1">
                <em>No folder</em> {/* Italicize for better UX */}
              </MenuItem>

              {/* Dynamically generated folders */}
              {folders.map((folder) => (
                <MenuItem key={folder.id} value={folder.id}>
                  {folder.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Button
          disabled={!title || !value}
          onClick={handleClick}
          variant={'contained'}
        >
          Create screen
        </Button>
      </Box>
      <Box
        position={'relative'}
        maxWidth={'100vw'}
        overflow={'auto'}
        maxHeight={`calc(100vh - 200px)`}
        paddingTop={'50px'}
        paddingBottom={'550px'}
        sx={{
          overflowX: 'hidden',
          paddingRight: 12,
        }}
      >
        <Editor
          cellPlugins={[...customPlugins]}
          value={value}
          onChange={handleEditorChange}
        />
      </Box>
    </Box>
  );
}

function parseUrl(url: string) {
  const regex = /\/admin\/tenants\/(\d+)\/companies\/(\d+)\/screens\/create/;
  const match = RegExp(regex).exec(url);

  if (match) {
    const tenantId = match[1];
    const companyId = match[2];

    return { tenantId, companyId };
  } else {
    return null;
  }
}
